/**
 * Guardian profile completion (plan §5.8.2).
 *
 * Step A "Tell us about you": first name(s), optional middle name, family name.
 * Step B "Add your email": email → 6-digit code → verify.
 * Shown by ProfileGate only for what the server reports as missing. No skip.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../contexts/AuthContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import { useLanguageContext } from '../../contexts/LanguageContext';
import { LanguageSelector } from '../layout/LanguageSelector';
import type { GuardianProfileField } from '../../types/Guardian';
import {
  apiErrorCode,
  requestEmailChange,
  updateMyProfile,
  verifyEmailChange,
} from '../../services/guardianProfileService';

const NAME_MAX = 100;
const RESEND_SECONDS = 60;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ERROR_KEYS: Record<string, string> = {
  IDENTITY_FIELDS_LOCKED: 'profile.errors.identityLocked',
  INVALID_NAME: 'profile.errors.nameInvalid',
  EMAIL_IN_USE: 'profile.errors.emailInUse',
  INVALID_CODE: 'profile.errors.codeInvalid',
  RATE_LIMIT: 'profile.errors.rateLimited',
  VALIDATION_ERROR: 'profile.errors.emailInvalid',
};

const inputClass =
  'w-full rounded-lg border-2 px-3 py-2.5 text-base focus:outline-none focus:ring-2 disabled:bg-gray-50 disabled:text-gray-600';

interface Props {
  missing: GuardianProfileField[];
}

const GuardianProfileCompletion: React.FC<Props> = ({ missing }) => {
  const { t } = useTranslation();
  const { logout } = useAuthContext();
  const { theme } = useThemeContext();
  const { isRTL } = useLanguageContext();

  const needsAbout = missing.includes('first_name') || missing.includes('last_name');
  const needsEmail = missing.includes('email');
  // Total steps are fixed at mount so "Step 2 of 2" stays right after Step A.
  const [total] = useState(() => (needsAbout ? 1 : 0) + (needsEmail ? 1 : 0));
  const current = needsAbout ? 1 : total;

  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, [needsAbout]);

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ direction: isRTL ? 'rtl' : 'ltr', backgroundColor: theme?.colors.background }}
    >
      <header className="py-4 px-6 shadow-sm bg-white">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <span className="text-xl font-semibold" style={{ color: theme?.colors.primary }}>
            {theme?.brandName}
          </span>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <button
              type="button"
              onClick={() => logout()}
              className="text-sm underline"
              style={{ color: theme?.colors.textSecondary }}
            >
              {t('profile.signOut')}
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-start sm:items-center justify-center py-10 px-4">
        <div
          className="bg-white rounded-xl shadow-lg p-6 sm:p-8 w-full max-w-md"
          style={{ borderTop: `4px solid ${theme?.colors.primary}` }}
        >
          <p className="text-sm font-medium mb-1" style={{ color: theme?.colors.textSecondary }}>
            {total > 1 ? t('profile.stepOf', { current, total }) : t('profile.title')}
          </p>
          {needsAbout ? (
            <AboutStep headingRef={headingRef} />
          ) : (
            <EmailStep headingRef={headingRef} />
          )}
          {total > 1 && (
            <div className="mt-6 flex gap-2" aria-hidden="true">
              {Array.from({ length: total }, (_, i) => (
                <span
                  key={i}
                  className="h-1.5 flex-1 rounded-full"
                  style={{
                    backgroundColor: i < current ? theme?.colors.primary : theme?.colors.accent || '#e5e7eb',
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Steps and small building blocks (module scope so they keep state across
// parent re-renders, e.g. a language switch).
// ---------------------------------------------------------------------------

function AboutStep({ headingRef: ref }: { headingRef: React.RefObject<HTMLHeadingElement> }) {
  const { t } = useTranslation();
  const { theme } = useThemeContext();
  const { guardian, setGuardian } = useAuthContext();
  const lockedFirst = !!(guardian?.verifiedIdentity && guardian.firstName?.trim());
  const lockedLast = !!(guardian?.verifiedIdentity && guardian.lastName?.trim());
  const [first, setFirst] = useState(guardian?.firstName ?? '');
  const [middle, setMiddle] = useState(guardian?.middleName ?? '');
  const [last, setLast] = useState(guardian?.lastName ?? '');
  const [errors, setErrors] = useState<{ first?: string; last?: string; form?: string }>({});
  const [busy, setBusy] = useState(false);
  const firstRef = useRef<HTMLInputElement>(null);
  const lastRef = useRef<HTMLInputElement>(null);

  const check = (value: string, requiredKey: string): string | undefined => {
    const v = value.trim();
    if (!v) return t(requiredKey);
    if (v.length > NAME_MAX) return t('profile.errors.nameTooLong');
    if (/[<>]/.test(v)) return t('profile.errors.nameInvalid');
    return undefined;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next = {
      first: check(first, 'profile.errors.firstNameRequired'),
      last: check(last, 'profile.errors.familyNameRequired'),
    };
    if (/[<>]/.test(middle)) next.first = next.first || t('profile.errors.nameInvalid');
    setErrors(next);
    if (next.first) return firstRef.current?.focus();
    if (next.last) return lastRef.current?.focus();

    const body: Record<string, string> = {};
    if (first.trim() !== (guardian?.firstName ?? '')) body.first_name = first.trim();
    if (middle.trim() !== (guardian?.middleName ?? '')) body.middle_name = middle.trim();
    if (last.trim() !== (guardian?.lastName ?? '')) body.last_name = last.trim();
    setBusy(true);
    try {
      setGuardian(await updateMyProfile(body));
    } catch (err) {
      const code = apiErrorCode(err);
      setErrors({ form: t(ERROR_KEYS[code ?? ''] ?? 'profile.errors.generic') });
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate>
      <h1 ref={ref} tabIndex={-1} className="text-xl font-bold mb-1" style={{ color: theme?.colors.text, outline: "none" }}>
        {t('profile.about.title')}
      </h1>
      <p className="text-sm mb-6" style={{ color: theme?.colors.textSecondary }}>
        {t('profile.subtitle')}
      </p>

      <Field
        id="pf-first"
        label={t('profile.about.firstName')}
        help={lockedFirst ? t('profile.about.verifiedLocked') : t('profile.about.firstNameHelp')}
        error={errors.first}
      >
        <input
          ref={firstRef}
          id="pf-first"
          autoComplete="given-name"
          maxLength={NAME_MAX}
          value={first}
          readOnly={lockedFirst}
          disabled={busy}
          onChange={(e) => setFirst(e.target.value)}
          aria-invalid={!!errors.first}
          aria-describedby="pf-first-help"
          className={inputClass}
        />
      </Field>

      <Field id="pf-middle" label={`${t('profile.about.middleName')} (${t('common.optional')})`}>
        <input
          id="pf-middle"
          autoComplete="additional-name"
          maxLength={NAME_MAX}
          value={middle}
          disabled={busy}
          onChange={(e) => setMiddle(e.target.value)}
          className={inputClass}
        />
      </Field>

      <Field
        id="pf-last"
        label={t('profile.about.familyName')}
        help={lockedLast ? t('profile.about.verifiedLocked') : t('profile.about.familyNameHelp')}
        error={errors.last}
      >
        <input
          ref={lastRef}
          id="pf-last"
          autoComplete="family-name"
          maxLength={NAME_MAX}
          value={last}
          readOnly={lockedLast}
          disabled={busy}
          onChange={(e) => setLast(e.target.value)}
          aria-invalid={!!errors.last}
          aria-describedby="pf-last-help"
          className={inputClass}
        />
      </Field>

      {errors.form && (
        <p role="alert" className="text-sm mb-4" style={{ color: theme?.colors.error }}>
          {errors.form}
        </p>
      )}
      <PrimaryButton busy={busy}>{t('profile.about.continue')}</PrimaryButton>
    </form>
  );
}

function EmailStep({ headingRef: ref }: { headingRef: React.RefObject<HTMLHeadingElement> }) {
  const { t } = useTranslation();
  const { theme } = useThemeContext();
  const { setGuardian, refreshGuardian, logout, isKioskMode } = useAuthContext();
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [minutes, setMinutes] = useState(10);
  const [code, setCode] = useState('');
  const [timer, setTimer] = useState(0);
  const [error, setError] = useState<{ text: string; code?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (timer <= 0) return;
    const id = setTimeout(() => setTimer((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [timer]);

  useEffect(() => {
    if (sentTo) codeRef.current?.focus();
  }, [sentTo]);

  const fail = (err: unknown) => {
    const c = apiErrorCode(err);
    if (c === 'EMAIL_UNCHANGED') {
      refreshGuardian();
      return;
    }
    setError({ text: t(ERROR_KEYS[c ?? ''] ?? 'profile.errors.generic'), code: c });
  };

  const send = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const value = email.trim().toLowerCase();
    if (!EMAIL_RE.test(value)) {
      setError({ text: t('profile.errors.emailInvalid') });
      emailRef.current?.focus();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await requestEmailChange(value);
      setMinutes(res.expiresInMinutes || 10);
      setSentTo(value);
      setCode('');
      setTimer(RESEND_SECONDS);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sentTo || !/^\d{6}$/.test(code)) {
      setError({ text: t('profile.errors.codeInvalid') });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setGuardian(await verifyEmailChange(sentTo, code));
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };

  const errorBlock = error && (
    <div role="alert" className="text-sm mb-4" style={{ color: theme?.colors.error }}>
      <p id="pf-email-error">{error.text}</p>
      {error.code === 'EMAIL_IN_USE' && (
        <button
          type="button"
          onClick={() => logout()}
          className="mt-2 w-full py-2.5 rounded-lg border-2 font-medium"
          style={{ borderColor: theme?.colors.primary, color: theme?.colors.primary }}
        >
          {t('profile.signOut')}
        </button>
      )}
    </div>
  );

  return (
    <div>
      <h1 ref={ref} tabIndex={-1} className="text-xl font-bold mb-1" style={{ color: theme?.colors.text, outline: "none" }}>
        {t('profile.email.title')}
      </h1>
      <p className="text-sm mb-6" style={{ color: theme?.colors.textSecondary }}>
        {t('profile.email.why')}
      </p>

      {!sentTo ? (
        <form onSubmit={send} noValidate>
          <Field id="pf-email" label={t('profile.email.label')}>
            <input
              ref={emailRef}
              id="pf-email"
              type="email"
              dir="ltr"
              autoComplete="email"
              inputMode="email"
              maxLength={254}
              value={email}
              disabled={busy}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={!!error}
              aria-describedby={error ? 'pf-email-error' : undefined}
              className={inputClass}
            />
          </Field>
          {errorBlock}
          <PrimaryButton busy={busy}>{t('profile.email.sendCode')}</PrimaryButton>
        </form>
      ) : (
        <form onSubmit={verify} noValidate>
          <p className="text-sm mb-4" style={{ color: theme?.colors.text }}>
            {t('profile.email.codeSent', { email: sentTo, minutes })}
          </p>
          {isKioskMode && (
            <p className="text-sm mb-4 rounded-lg bg-blue-50 p-3" style={{ color: theme?.colors.text }}>
              {t('profile.email.kioskHint')}
            </p>
          )}
          <Field id="pf-code" label={t('auth.enterCode')}>
            <input
              ref={codeRef}
              id="pf-code"
              dir="ltr"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              value={code}
              disabled={busy}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              aria-invalid={!!error}
              aria-describedby={error ? 'pf-email-error' : undefined}
              className={`${inputClass} text-center text-2xl tracking-[0.5em] font-bold`}
            />
          </Field>
          {errorBlock}
          <PrimaryButton busy={busy} disabled={code.length !== 6}>
            {t('profile.email.verify')}
          </PrimaryButton>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-sm">
            {timer > 0 ? (
              <span style={{ color: theme?.colors.textSecondary }}>{t('auth.resendIn', { seconds: timer })}</span>
            ) : (
              <button type="button" onClick={() => send()} disabled={busy} className="underline" style={{ color: theme?.colors.primary }}>
                {t('auth.resendCode')}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setSentTo(null);
                setError(null);
                setTimeout(() => emailRef.current?.focus(), 0);
              }}
              className="underline"
              style={{ color: theme?.colors.primary }}
            >
              {t('profile.email.changeEmail')}
            </button>
          </div>
        </form>
      )}

      <p className="mt-6 text-sm" style={{ color: theme?.colors.textSecondary }}>
        {t('profile.email.noEmailHelp')}
      </p>
    </div>
  );
}

function Field({
  id,
  label,
  help,
  error,
  children,
}: {
  id: string;
  label: string;
  help?: string;
  error?: string;
  children: React.ReactNode;
}) {
  const { theme } = useThemeContext();
  return (
    <div className="mb-4">
      <label htmlFor={id} className="block text-sm font-medium mb-1" style={{ color: theme?.colors.text }}>
        {label}
      </label>
      {children}
      {(error || help) && (
        <p
          id={`${id}-help`}
          className="mt-1 text-xs"
          style={{ color: error ? theme?.colors.error : theme?.colors.textSecondary }}
        >
          {error || help}
        </p>
      )}
    </div>
  );
}

function PrimaryButton({
  busy,
  disabled,
  children,
}: {
  busy: boolean;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const { theme } = useThemeContext();
  return (
    <button
      type="submit"
      disabled={busy || disabled}
      className="w-full py-3 px-4 rounded-lg font-medium text-white transition-colors disabled:opacity-50 min-h-[48px]"
      style={{ backgroundColor: theme?.colors.primary }}
    >
      {busy ? t('common.loading') : children}
    </button>
  );
}

export default GuardianProfileCompletion;
